import { LocationsFieldItem, UnresolvedLocationsFieldItem } from 'components/NewForm/LocationsFieldTypes';
import { CountryList } from 'lib/enum/country.enum';
import { FacebookCountryCode } from 'lib/enum/facebook-country-code-enum';

export enum UnresolvedTargetingLocationType {
    UnresolvedCity = 'unresolved_city',
}

export enum TargetingLocationType {
    Country = 'country',
    Region = 'region',
    City = 'city',
}

export type TargetingLocationForApiCall =
    | {
          type: TargetingLocationType.Country;
          facebookKey: null;
          country: FacebookCountryCode;
          radius: null;
      }
    | {
          type: TargetingLocationType.Region;
          facebookKey: string;
          country: null;
          radius: null;
      }
    | {
          type: TargetingLocationType.City;
          facebookKey: string;
          country: null;
          radius: number;
      };

export type UnresolvedTargetingLocationForApiCall = {
    type: UnresolvedTargetingLocationType.UnresolvedCity;
    facebookKey: null;
    country: FacebookCountryCode;
    name: string;
    radius: number;
};

export type TargetingLocation =
    | {
          country: FacebookCountryCode;
          metaKey: string;
          name: string;
          type: TargetingLocationType.Region;
      }
    | {
          country: FacebookCountryCode;
          metaKey: undefined;
          name: undefined;
          type: TargetingLocationType.Country;
      }
    | {
          country: FacebookCountryCode;
          metaKey: string;
          metaRegionKey: string | null;
          name: string;
          radius: number;
          regionName: string | null;
          type: TargetingLocationType.City;
      };

export type LegacyTargetingLocationWithFacebookKeyField =
    | {
          country: FacebookCountryCode;
          facebookKey: string;
          name: string;
          type: TargetingLocationType.Region;
      }
    | {
          country: FacebookCountryCode;
          facebookKey: undefined;
          name: undefined;
          type: TargetingLocationType.Country;
      }
    | {
          country: FacebookCountryCode;
          facebookKey: string;
          metaRegionKey: string | null;
          name: string;
          radius: number;
          regionName: string | null;
          type: TargetingLocationType.City;
      };

export type UnresolvedTargetingLocation = {
    country: FacebookCountryCode;
    metaKey: undefined;
    name: string;
    radius: number;
    type: UnresolvedTargetingLocationType.UnresolvedCity;
};

export const getTargetingLocationDisplayName = (
    targetingLocation: TargetingLocation | UnresolvedTargetingLocation,
    showRadius: boolean = false,
): string => {
    switch (targetingLocation.type) {
        case TargetingLocationType.Country:
            return CountryList[targetingLocation.country];
        case TargetingLocationType.Region:
            return `${targetingLocation.name}, ${CountryList[targetingLocation.country]}`;
        case TargetingLocationType.City:
            return `${targetingLocation.name} ${showRadius ? ` (+${targetingLocation.radius}km)` : ''}, ${
                CountryList[targetingLocation.country]
            }`;
        case UnresolvedTargetingLocationType.UnresolvedCity:
            return targetingLocation.name;
    }
};

export const getTargetingLocationsShortDisplayName = (
    targetingLocation: TargetingLocation | UnresolvedTargetingLocation,
    showRadius: boolean = false,
): string => {
    switch (targetingLocation.type) {
        case TargetingLocationType.Country:
            return CountryList[targetingLocation.country];
        case TargetingLocationType.Region:
            return `${targetingLocation.name}`;
        case TargetingLocationType.City:
            return `${targetingLocation.name}${showRadius ? ` (+${targetingLocation.radius}km)` : ''}`;
        case UnresolvedTargetingLocationType.UnresolvedCity:
            return targetingLocation.name;
    }
};

export function getTargetingLocationsForApiCall(locations: LocationsFieldItem[]): TargetingLocationForApiCall[];
export function getTargetingLocationsForApiCall(
    locations: Array<LocationsFieldItem | UnresolvedLocationsFieldItem>,
): Array<TargetingLocationForApiCall | UnresolvedTargetingLocationForApiCall>;
export function getTargetingLocationsForApiCall(locations: any[]): unknown[] {
    return locations.map(location => {
        switch (location.type) {
            case TargetingLocationType.Country:
                return {
                    type: TargetingLocationType.Country,
                    country: location.countryCode,
                };
            case TargetingLocationType.Region:
                return {
                    type: TargetingLocationType.Region,
                    facebookKey: location.key,
                };
            case TargetingLocationType.City:
                return {
                    type: TargetingLocationType.City,
                    facebookKey: location.key,
                    radius: location.radius,
                };
            default:
                return {
                    type: UnresolvedTargetingLocationType.UnresolvedCity,
                    country: location.countryCode,
                    name: location.name,
                    radius: location.radius,
                };
        }
    });
}
